import React, { useEffect, useState } from 'react';
import { adminService } from '../services/api';
import styles from './Admin.module.css';

const EMPTY_USER = { name: '', email: '', password: '', role: 'free_user', governorate_id: '', region_id: '' };

export default function Admin() {
    const [users, setUsers] = useState([]);
    const [governorates, setGovernorates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [newGov, setNewGov] = useState('');
    const [newCat, setNewCat] = useState('');
    const [tab, setTab] = useState('users');

    // Governorate edit state
    const [editingGovId, setEditingGovId] = useState(null);
    const [editingGovName, setEditingGovName] = useState('');
    const [newRegionFor, setNewRegionFor] = useState({});
    const [editingRegion, setEditingRegion] = useState(null); // { id, name }

    const [showAddUser, setShowAddUser] = useState(false);
    const [newUser, setNewUser] = useState(EMPTY_USER);
    const [addUserError, setAddUserError] = useState('');
    const [addUserLoading, setAddUserLoading] = useState(false);

    // Category edit/sub-category add state
    const [editingCatId, setEditingCatId] = useState(null);
    const [editingCatName, setEditingCatName] = useState('');
    const [newSubFor, setNewSubFor] = useState({});

    useEffect(() => {
        adminService.listUsers().then((r) => setUsers(r.data)).catch(console.error);
        adminService.governorates().then((r) => setGovernorates(r.data)).catch(console.error);
        adminService.categories().then((r) => setCategories(r.data)).catch(console.error);
    }, []);

    const updateRole = async (id, role) => {
        await adminService.updateUser(id, { role });
        setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
    };

    const updateRegion = async (id, region_id) => {
        await adminService.updateUser(id, { region_id: region_id || null });
        const region = governorates.flatMap((g) => g.regions || []).find((r) => r.id === parseInt(region_id));
        setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, region_id, region: region || null } : u)));
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setAddUserError('');
        setAddUserLoading(true);
        try {
            const payload = {
                name: newUser.name,
                email: newUser.email,
                password: newUser.password,
                role: newUser.role,
                governorate_id: newUser.governorate_id || null,
                region_id: newUser.region_id || null,
            };
            const res = await adminService.createAdmin(payload);
            setUsers((prev) => [...prev, res.data]);
            setNewUser(EMPTY_USER);
            setShowAddUser(false);
        } catch (err) {
            const errors = err.response?.data?.errors;
            setAddUserError(errors
                ? Object.values(errors).flat().join(' | ')
                : err.response?.data?.message || 'حدث خطأ');
        } finally {
            setAddUserLoading(false);
        }
    };

    const addGovernorate = async (e) => {
        e.preventDefault();
        const res = await adminService.storeGovernorate(newGov);
        setGovernorates((prev) => [...prev, res.data]);
        setNewGov('');
    };

    const addCategory = async (e) => {
        e.preventDefault();
        const res = await adminService.storeCategory(newCat);
        setCategories((prev) => [...prev, { ...res.data, sub_categories: [] }]);
        setNewCat('');
    };

    const saveEditCat = async (id) => {
        if (!editingCatName.trim()) return;
        await adminService.updateCategory(id, editingCatName.trim());
        setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, name: editingCatName.trim() } : c)));
        setEditingCatId(null);
        setEditingCatName('');
    };

    const deleteCat = async (id) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) return;
        await adminService.destroyCategory(id);
        setCategories((prev) => prev.filter((c) => c.id !== id));
    };

    const addSubCat = async (e, catId) => {
        e.preventDefault();
        const name = newSubFor[catId] || '';
        if (!name.trim()) return;
        const res = await adminService.storeSubCategory({ name: name.trim(), category_id: catId });
        setCategories((prev) => prev.map((c) =>
            c.id === catId ? { ...c, sub_categories: [...(c.sub_categories || []), res.data] } : c
        ));
        setNewSubFor((f) => ({ ...f, [catId]: '' }));
    };

    const deleteSubCat = async (catId, subId) => {
        await adminService.destroySubCategory(subId);
        setCategories((prev) => prev.map((c) =>
            c.id === catId ? { ...c, sub_categories: c.sub_categories.filter((s) => s.id !== subId) } : c
        ));
    };

    const saveEditGov = async (id) => {
        if (!editingGovName.trim()) return;
        await adminService.updateGovernorate(id, editingGovName.trim());
        setGovernorates((prev) => prev.map((g) => (g.id === id ? { ...g, name: editingGovName.trim() } : g)));
        setEditingGovId(null);
        setEditingGovName('');
    };

    const deleteGov = async (id) => {
        if (!window.confirm('هل أنت متأكد؟ سيتم حذف جميع المناطق المرتبطة بهذه المحافظة.')) return;
        await adminService.destroyGovernorate(id);
        setGovernorates((prev) => prev.filter((g) => g.id !== id));
    };

    const addRegionToGov = async (e, govId) => {
        e.preventDefault();
        const name = newRegionFor[govId] || '';
        if (!name.trim()) return;
        const res = await adminService.storeRegion({ name: name.trim(), governorate_id: govId });
        setGovernorates((prev) => prev.map((g) =>
            g.id === govId ? { ...g, regions: [...(g.regions || []), res.data] } : g
        ));
        setNewRegionFor((f) => ({ ...f, [govId]: '' }));
    };

    const saveEditRegion = async (govId, regionId) => {
        if (!editingRegion?.name.trim()) return;
        await adminService.updateRegion(regionId, editingRegion.name.trim());
        setGovernorates((prev) => prev.map((g) =>
            g.id === govId
                ? { ...g, regions: g.regions.map((r) => (r.id === regionId ? { ...r, name: editingRegion.name.trim() } : r)) }
                : g
        ));
        setEditingRegion(null);
    };

    const deleteRegion = async (govId, regionId) => {
        await adminService.destroyRegion(regionId);
        setGovernorates((prev) => prev.map((g) =>
            g.id === govId ? { ...g, regions: g.regions.filter((r) => r.id !== regionId) } : g
        ));
    };

    const TABS = [
        { key: 'users', label: `المستخدمون (${users.length})` },
        { key: 'geo', label: 'المحافظات والمناطق' },
        { key: 'categories', label: 'التصنيفات' },
    ];

    return (
        <div className={`${styles.page} text-ar`}>
            <div className={styles.pageHeader}>
                <h1>لوحة الإدارة</h1>
            </div>

            <div className={styles.tabs}>
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Users Tab */}
            {tab === 'users' && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h3>إدارة المستخدمين</h3>
                        <button
                            className={styles.addBtn}
                            onClick={() => { setShowAddUser((v) => !v); setAddUserError(''); }}
                        >
                            {showAddUser ? '✕ إلغاء' : '+ إضافة مستخدم'}
                        </button>
                    </div>

                    {showAddUser && (
                        <div className={styles.addForm}>
                            <h4>مستخدم جديد</h4>
                            {addUserError && <div className={styles.formError}>{addUserError}</div>}
                            <form onSubmit={handleAddUser}>
                                <div className={styles.formGrid}>
                                    <div className={styles.field}>
                                        <label className={styles.label}>الاسم</label>
                                        <input className={styles.input} value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="الاسم الكامل" required />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.label}>البريد الإلكتروني</label>
                                        <input className={styles.input} type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" required />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.label}>كلمة المرور</label>
                                        <input className={styles.input} type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="8 أحرف على الأقل" required minLength={8} />
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.label}>الدور</label>
                                        <select className={styles.input} value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                                            <option value="free_user">مستخدم حر</option>
                                            <option value="region_admin">مشرف منطقة</option>
                                            <option value="super_admin">مشرف عام</option>
                                            <option value="monitor">مراقب</option>
                                        </select>
                                    </div>
                                    <div className={styles.field}>
                                        <label className={styles.label}>المحافظة</label>
                                        <select className={styles.input} value={newUser.governorate_id} onChange={(e) => setNewUser({ ...newUser, governorate_id: e.target.value, region_id: '' })}>
                                            <option value="">— اختياري —</option>
                                            {governorates.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                        </select>
                                    </div>
                                    {newUser.role === 'region_admin' && (
                                        <div className={styles.field}>
                                            <label className={styles.label}>المنطقة المخصصة</label>
                                            <select className={styles.input} value={newUser.region_id} onChange={(e) => setNewUser({ ...newUser, region_id: e.target.value })} disabled={!newUser.governorate_id}>
                                                <option value="">— اختر المنطقة —</option>
                                                {(governorates.find((g) => g.id === parseInt(newUser.governorate_id))?.regions || []).map((r) => (
                                                    <option key={r.id} value={r.id}>{r.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <button className={styles.saveBtn} type="submit" disabled={addUserLoading}>
                                    {addUserLoading ? 'جاري الحفظ...' : 'حفظ المستخدم'}
                                </button>
                            </form>
                        </div>
                    )}

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {['الاسم', 'البريد الإلكتروني', 'الدور', 'المحافظة', 'المنطقة', 'تغيير الدور'].map((h) => (
                                        <th key={h} className={styles.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} className={styles.tr}>
                                        <td className={styles.td}>{u.name}</td>
                                        <td className={`${styles.td} ${styles.tdLtr}`}>{u.email}</td>
                                        <td className={styles.td}>
                                            <span className={`${styles.badge} ${styles[`badge_${u.role}`]}`}>
                                                {roleLabel(u.role)}
                                            </span>
                                        </td>
                                        <td className={styles.td}>{u.governorate?.name || '—'}</td>
                                        <td className={styles.td}>
                                            {u.role === 'region_admin' ? (
                                                <select
                                                    value={u.region_id || ''}
                                                    onChange={(e) => updateRegion(u.id, e.target.value)}
                                                    className={styles.roleSelect}
                                                >
                                                    <option value="">— بدون —</option>
                                                    {(governorates.find((g) => g.id === u.governorate_id)?.regions || []).map((r) => (
                                                        <option key={r.id} value={r.id}>{r.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className={styles.tdMuted}>—</span>
                                            )}
                                        </td>
                                        <td className={styles.td}>
                                            <select
                                                value={u.role}
                                                onChange={(e) => updateRole(u.id, e.target.value)}
                                                className={styles.roleSelect}
                                            >
                                                <option value="free_user">مستخدم حر</option>
                                                <option value="region_admin">مشرف منطقة</option>
                                                <option value="super_admin">مشرف عام</option>
                                                <option value="monitor">مراقب</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Geo Tab */}
            {tab === 'geo' && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h3>إدارة المحافظات والمناطق</h3>
                    </div>

                    <div className={styles.addRow}>
                        <h4>إضافة محافظة جديدة</h4>
                        <form onSubmit={addGovernorate} className={styles.inlineForm}>
                            <input className={styles.input} value={newGov} onChange={(e) => setNewGov(e.target.value)} placeholder="اسم المحافظة" required />
                            <button className={styles.saveBtn} type="submit">إضافة</button>
                        </form>
                    </div>

                    <div className={styles.listWrap}>
                        {governorates.map((g) => (
                            <div key={g.id} className={styles.catItem}>
                                {/* Governorate header */}
                                <div className={styles.catHeader}>
                                    {editingGovId === g.id ? (
                                        <div className={styles.inlineForm}>
                                            <input
                                                className={styles.input}
                                                value={editingGovName}
                                                onChange={(e) => setEditingGovName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && saveEditGov(g.id)}
                                                autoFocus
                                            />
                                            <button className={styles.saveBtn} type="button" onClick={() => saveEditGov(g.id)}>حفظ</button>
                                            <button className={styles.cancelIconBtn} type="button" onClick={() => setEditingGovId(null)}>✕</button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className={styles.govName}>{g.name}</span>
                                            <div className={styles.catActions}>
                                                <button className={styles.iconBtn} onClick={() => { setEditingGovId(g.id); setEditingGovName(g.name); }}>✏</button>
                                                <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={() => deleteGov(g.id)}>🗑</button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Regions as chips */}
                                <div className={styles.subCatList}>
                                    {g.regions?.map((r) => (
                                        <span key={r.id} className={styles.subCatChip}>
                                            {editingRegion?.id === r.id ? (
                                                <span className={styles.inlineForm} style={{ margin: 0 }}>
                                                    <input
                                                        className={`${styles.input} ${styles.subInput}`}
                                                        value={editingRegion.name}
                                                        onChange={(e) => setEditingRegion({ ...editingRegion, name: e.target.value })}
                                                        onKeyDown={(e) => e.key === 'Enter' && saveEditRegion(g.id, r.id)}
                                                        autoFocus
                                                        style={{ width: 100 }}
                                                    />
                                                    <button className={styles.chipRemove} onClick={() => saveEditRegion(g.id, r.id)} title="حفظ">✓</button>
                                                    <button className={styles.chipRemove} onClick={() => setEditingRegion(null)} title="إلغاء">✕</button>
                                                </span>
                                            ) : (
                                                <>
                                                    <span
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setEditingRegion({ id: r.id, name: r.name })}
                                                        title="انقر للتعديل"
                                                    >
                                                        {r.name}
                                                    </span>
                                                    <button className={styles.chipRemove} onClick={() => deleteRegion(g.id, r.id)}>×</button>
                                                </>
                                            )}
                                        </span>
                                    ))}
                                    <form onSubmit={(e) => addRegionToGov(e, g.id)} className={styles.subCatAdd}>
                                        <input
                                            className={`${styles.input} ${styles.subInput}`}
                                            value={newRegionFor[g.id] || ''}
                                            onChange={(e) => setNewRegionFor((f) => ({ ...f, [g.id]: e.target.value }))}
                                            placeholder="+ منطقة جديدة"
                                        />
                                        <button className={styles.saveBtn} type="submit">إضافة</button>
                                    </form>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Categories Tab */}
            {tab === 'categories' && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h3>إدارة التصنيفات</h3>
                    </div>

                    <div className={styles.addRow}>
                        <h4>إضافة تصنيف رئيسي</h4>
                        <form onSubmit={addCategory} className={styles.inlineForm}>
                            <input className={styles.input} value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="اسم التصنيف" required />
                            <button className={styles.saveBtn} type="submit">إضافة</button>
                        </form>
                    </div>

                    <div className={styles.listWrap}>
                        {categories.map((c) => (
                            <div key={c.id} className={styles.catItem}>
                                {/* Category header */}
                                <div className={styles.catHeader}>
                                    {editingCatId === c.id ? (
                                        <div className={styles.inlineForm}>
                                            <input
                                                className={styles.input}
                                                value={editingCatName}
                                                onChange={(e) => setEditingCatName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && saveEditCat(c.id)}
                                                autoFocus
                                            />
                                            <button className={styles.saveBtn} type="button" onClick={() => saveEditCat(c.id)}>حفظ</button>
                                            <button className={styles.cancelIconBtn} type="button" onClick={() => setEditingCatId(null)}>✕</button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className={styles.govName}>{c.name}</span>
                                            <div className={styles.catActions}>
                                                <button className={styles.iconBtn} onClick={() => { setEditingCatId(c.id); setEditingCatName(c.name); }}>✏</button>
                                                <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={() => deleteCat(c.id)}>🗑</button>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Sub-categories */}
                                <div className={styles.subCatList}>
                                    {c.sub_categories?.map((s) => (
                                        <span key={s.id} className={styles.subCatChip}>
                                            {s.name}
                                            <button className={styles.chipRemove} onClick={() => deleteSubCat(c.id, s.id)}>×</button>
                                        </span>
                                    ))}
                                    <form onSubmit={(e) => addSubCat(e, c.id)} className={styles.subCatAdd}>
                                        <input
                                            className={`${styles.input} ${styles.subInput}`}
                                            value={newSubFor[c.id] || ''}
                                            onChange={(e) => setNewSubFor((f) => ({ ...f, [c.id]: e.target.value }))}
                                            placeholder="+ تصنيف فرعي"
                                        />
                                        <button className={styles.saveBtn} type="submit">إضافة</button>
                                    </form>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function roleLabel(role) {
    return { free_user: 'مستخدم حر', region_admin: 'مشرف منطقة', super_admin: 'مشرف عام', monitor: 'مراقب' }[role] || role;
}
